import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ photos, index, onClose, onIndex }) {
  const [dir, setDir] = React.useState(0);
  const go = React.useCallback((d) => {
    setDir(d);
    onIndex((i) => (i + d + photos.length) % photos.length);
  }, [photos.length, onIndex]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [go, onClose]);

  const p = photos[index];
  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex flex-col bg-navy-950/95 backdrop-blur-xl" role="dialog" aria-modal="true" aria-label="Event photo viewer" data-testid="gallery-lightbox">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-mono text-[12px] tracking-[0.2em] text-on-dark-muted" data-testid="lightbox-counter">{index + 1} / {photos.length}</span>
        <button onClick={onClose} aria-label="Close" data-testid="lightbox-close" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"><X size={20} /></button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.img key={p.id} src={p.url} alt={p.caption || "U-Hackathon 2026 event photo"} custom={dir}
            initial={{ opacity: 0, x: dir * 120 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -120 }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.6}
            onDragEnd={(_, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}
            className="max-h-full max-w-full cursor-grab select-none rounded-xl object-contain active:cursor-grabbing" draggable={false} data-testid="lightbox-image" />
        </AnimatePresence>
        {photos.length > 1 && <>
          <button onClick={() => go(-1)} aria-label="Previous photo" data-testid="lightbox-prev" className="absolute left-3 hidden h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 sm:flex"><ChevronLeft size={24} /></button>
          <button onClick={() => go(1)} aria-label="Next photo" data-testid="lightbox-next" className="absolute right-3 hidden h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 sm:flex"><ChevronRight size={24} /></button>
        </>}
      </div>
      <p className="min-h-[56px] px-6 py-4 text-center text-[15px] text-white/90" data-testid="lightbox-caption">{p.caption}</p>
    </motion.div>,
    document.body
  );
}
